import { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { useEntitlements } from '../../hooks/useEntitlements';

interface FormSubmission {
    id: string;
    player_id?: string;
    play_session_id?: string;
    submitted_at?: string;
    created_at: string;
    data: Record<string, unknown>;
    player?: {
        id: string;
        name?: string;
        phone?: string;
        email?: string;
        ip_hash?: string;
    };
    ip_hash?: string;          // từ play_session
    reward_code?: string;
    claim_status?: string;
    result_type?: string;
    gift_name?: string;
    gift_tier?: string;
    shared_ip_count?: number;  // số người cùng IP
}

const TIER_ICONS: Record<string, string> = {
    bronze:   '🥉',
    silver:   '🥈',
    gold:     '🥇',
    platinum: '💎',
};

function formatDate(v?: string | null) {
    if (!v) return '—';
    return new Date(v).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
}

const SKIP_KEYS = ['id', 'created_at', 'updated_at', 'Khách chưa đặt tên'];
const BORING_VALUES = ['Khách chưa đặt tên', 'khách chưa đặt tên', ''];

function flattenData(data: Record<string, unknown>): string {
    return Object.entries(data)
        .filter(([k, v]) => !SKIP_KEYS.includes(k) && v !== null && v !== undefined && !BORING_VALUES.includes(String(v)))
        .map(([k, v]) => `${k}: ${v}`)
        .join(' | ');
}

function exportCSV(submissions: FormSubmission[]) {
    if (!submissions.length) return;

    const dataKeys = new Set<string>();
    submissions.forEach(s => Object.keys(s.data || {}).forEach(k => dataKeys.add(k)));

    const headers = ['#', 'Thời gian', 'Tên', 'SĐT', 'Email', ...Array.from(dataKeys), 'Kết quả', 'Mã thưởng'];
    const rows = submissions.map((s, i) => [
        String(i + 1),
        formatDate(s.submitted_at || s.created_at),
        s.player?.name || s.data?.name as string || '',
        s.player?.phone || s.data?.phone as string || '',
        s.player?.email || s.data?.email as string || '',
        ...Array.from(dataKeys).map(k => String(s.data?.[k] ?? '')),
        s.result_type === 'win' ? 'Trúng' : s.result_type === 'lose' ? 'Không trúng' : '—',
        s.reward_code || '',
    ]);

    const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

export default function FormSubmissionsTab({ campaignId, onAfterClaim }: { campaignId: string; onAfterClaim?: () => void }) {
    const { hasFeature, entitlements } = useEntitlements();
    const canExport = hasFeature('can_export');
    const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [filterResult, setFilterResult] = useState<'all' | 'win' | 'lose'>('all');

    // ── GIAO DIỆN XÁC MINH PHÒNG CHỐNG HACK UI/LEADS GIA ───────────────────
    const [verifyCode, setVerifyCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState<any | null>(null);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const [claiming, setClaiming] = useState(false);
    const [claimSuccess, setClaimSuccess] = useState(false);
    // Track per-row claimed codes
    const [claimedCodes, setClaimedCodes] = useState<Set<string>>(new Set());
    const [rowClaiming, setRowClaiming] = useState<string | null>(null);

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verifyCode.trim()) return;
        setVerifying(true);
        setVerifyError(null);
        setVerifyResult(null);
        setClaimSuccess(false);

        try {
            const res = await (api as any).request('/admin/rewards/verify', {
                method: 'POST',
                body: { reward_code: verifyCode.trim() },
            });
            if (res.success) {
                setVerifyResult(res.data);
            } else {
                setVerifyError(res.message || 'Mã trúng thưởng không tồn tại.');
            }
        } catch (err: any) {
            setVerifyError(err.message || 'Mã trúng thưởng không tồn tại hoặc không thuộc cửa hàng của bạn.');
        } finally {
            setVerifying(false);
        }
    };

    const handleClaim = async () => {
        if (!verifyResult) return;
        setClaiming(true);
        try {
            const res = await (api as any).request('/admin/rewards/claim', {
                method: 'POST',
                body: { reward_code: verifyResult.reward_code },
            });
            if (res.success) {
                setClaimSuccess(true);
                setVerifyResult((prev: any) => prev ? { ...prev, status: 'claimed', claimed_at: new Date().toISOString() } : null);
                // Tải lại bảng để cập nhật trạng thái dòng
                load();
                // Thông báo parent refresh gifts data (để cập nhật "X đã nhận")
                onAfterClaim?.();
            } else {
                alert(res.message || 'Xảy ra lỗi khi trao quà.');
            }
        } catch (err: any) {
            alert(err.message || 'Xảy ra lỗi khi trao quà.');
        } finally {
            setClaiming(false);
        }
    };

    // Quick inline claim per row
    const handleQuickClaim = async (rewardCode: string) => {
        if (rowClaiming || claimedCodes.has(rewardCode)) return;
        if (!confirm(`Xác nhận đã trao quà với mã: ${rewardCode}?`)) return;
        setRowClaiming(rewardCode);
        try {
            const res = await (api as any).request('/admin/rewards/claim', {
                method: 'POST',
                body: { reward_code: rewardCode },
            });
            if (res.success) {
                setClaimedCodes(prev => new Set([...prev, rewardCode]));
                // Thông báo parent refresh gifts data (để cập nhật "X đã nhận")
                onAfterClaim?.();
            } else {
                alert(res.message || 'Xảy ra lỗi khi trao quà.');
            }
        } catch (err: any) {
            alert(err.message || 'Xảy ra lỗi khi trao quà.');
        } finally {
            setRowClaiming(null);
        }
    };
    // ―――――――――――――――――――――――――――――――――――――――――――――――――――――――――――――――――――――─────────────────────────────────────────────────────────────────────

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = (await (api as any).request(
                `/admin/campaigns/${campaignId}/form-submissions`
            )) as { data: FormSubmission[] };
            const items = res.data || [];
            setSubmissions(items);
            // Khởi tạo claimedCodes từ DB — những mã đã được claim sẽ hiện badge ngay khi load
            const alreadyClaimed = new Set(
                items
                    .filter(s => s.claim_status === 'claimed' && s.reward_code)
                    .map(s => s.reward_code!)
            );
            setClaimedCodes(alreadyClaimed);
        } catch (e: unknown) {
            try {
                const res2 = (await (api as any).request(
                    `/admin/campaigns/${campaignId}/sessions?per_page=100`
                )) as { data: unknown[] };
                const sessions = (res2.data || []) as Array<{
                    id: string;
                    player_id?: string;
                    created_at: string;
                    started_at?: string;
                    client_result?: Record<string, unknown>;
                    server_result?: { win?: boolean };
                    status?: string;
                }>;
                setSubmissions(
                    sessions.map(s => ({
                        id: s.id,
                        player_id: s.player_id,
                        play_session_id: s.id,
                        created_at: s.created_at,
                        submitted_at: s.started_at,
                        data: s.client_result || {},
                        result_type: s.server_result?.win ? 'win' : 'lose',
                    }))
                );
            } catch {
                setError((e as Error).message || 'Không thể tải dữ liệu biểu mẫu');
            }
        } finally {
            setLoading(false);
        }
    }, [campaignId]);

    useEffect(() => { load(); }, [load]);

    const filtered = submissions.filter(s => {
        const matchResult = filterResult === 'all' || s.result_type === filterResult;
        const q = search.toLowerCase();
        const matchSearch = !q
            || s.player?.name?.toLowerCase().includes(q)
            || s.player?.phone?.includes(q)
            || s.player?.email?.toLowerCase().includes(q)
            || (s.data?.name as string)?.toLowerCase().includes(q)
            || (s.data?.phone as string)?.includes(q)
            || (s.data?.email as string)?.toLowerCase().includes(q)
            || s.id.includes(q);
        return matchResult && matchSearch;
    });

    if (loading) return (
        <div className="flex h-40 items-center justify-center text-gray-400">
            <svg className="mr-2 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Đang tải biểu mẫu...
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Tra cứu & Xác minh Quà tặng từ Server */}
            <div className="rounded-xl border border-indigo-100 bg-gradient-to-r from-indigo-50/70 to-purple-50/70 p-5 shadow-sm">
                <div className="flex items-center gap-2">
                    <span className="text-xl">🛡️</span>
                    <div>
                        <h3 className="font-semibold text-indigo-950">Tra cứu & Xác minh mã trúng thưởng thực tế</h3>
                        <p className="text-xs text-indigo-700">Đối chiếu mã trúng thưởng trực tiếp với cơ sở dữ liệu gốc để phòng ngừa hack giao diện (UI) hoặc dữ liệu giả.</p>
                    </div>
                </div>

                <form onSubmit={handleVerify} className="mt-4 flex gap-2">
                    <input
                        type="text"
                        placeholder="Nhập mã trúng thưởng của khách hàng (ví dụ: WINNER9999)..."
                        value={verifyCode}
                        onChange={e => setVerifyCode(e.target.value)}
                        className="flex-1 rounded-lg border border-indigo-200 px-3 py-2 text-sm uppercase placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                    />
                    <button
                        type="submit"
                        disabled={verifying}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {verifying ? 'Đang kiểm tra...' : '🔍 Xác minh'}
                    </button>
                </form>

                {/* Kết quả xác minh */}
                {verifyError && (
                    <div className="mt-3 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700 flex items-center gap-2">
                        <span>❌</span> {verifyError}
                    </div>
                )}

                {verifyResult && (
                    <div className="mt-4 rounded-lg border border-indigo-150 bg-white p-4 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 h-16 w-16 opacity-10">
                            <span className="text-6xl select-none">🎁</span>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Mã thưởng:</span>
                                <span className="font-mono text-base font-bold text-indigo-900 bg-indigo-50 px-2.5 py-0.5 rounded border border-indigo-100">{verifyResult.reward_code}</span>
                            </div>
                            <div>
                                {verifyResult.status === 'claimed' ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-0.5 text-xs font-semibold text-blue-800">
                                        🔵 Đã trao quà thực tế
                                    </span>
                                ) : verifyResult.status === 'expired' ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-0.5 text-xs font-semibold text-red-800">
                                        🔴 Hết hạn sử dụng
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-0.5 text-xs font-semibold text-green-800 animate-pulse">
                                        🟢 Hợp lệ - Chưa trao quà
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-y-3 sm:grid-cols-3 sm:gap-x-4 text-sm">
                            <div>
                                <span className="block text-xs font-medium text-gray-400 uppercase">Khách nhận quà:</span>
                                <span className="block font-semibold text-gray-800 mt-0.5">{verifyResult.player?.name || 'Vô danh'}</span>
                                <span className="block text-xs text-gray-500">{verifyResult.player?.phone || 'Không có SĐT'}</span>
                            </div>
                            <div>
                                <span className="block text-xs font-medium text-gray-400 uppercase">Món quà thực tế:</span>
                                <span className="block font-semibold text-amber-700 mt-0.5">🎁 {verifyResult.gift?.name || 'Không xác định'}</span>
                                <span className="block text-xs text-gray-500">Phân hạng: <span className="capitalize font-medium text-gray-700">{verifyResult.gift?.tier || 'default'}</span></span>
                            </div>
                            <div>
                                <span className="block text-xs font-medium text-gray-400 uppercase">Ngày trúng:</span>
                                <span className="block text-gray-700 mt-0.5">{verifyResult.assigned_at ? new Date(verifyResult.assigned_at).toLocaleString('vi-VN') : '—'}</span>
                                {verifyResult.claimed_at && (
                                    <span className="block text-xs text-blue-600 mt-0.5 font-medium">Nhận lúc: {new Date(verifyResult.claimed_at).toLocaleString('vi-VN')}</span>
                                )}
                            </div>
                        </div>

                        {/* Nút trao quà */}
                        {verifyResult.status === 'assigned' && (
                            <div className="mt-4 border-t border-gray-100 pt-3 flex justify-end">
                                <button
                                    onClick={handleClaim}
                                    disabled={claiming}
                                    className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                                >
                                    {claiming ? 'Đang xử lý...' : '🎁 Xác nhận đã trao quà'}
                                </button>
                            </div>
                        )}

                        {claimSuccess && (
                            <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800 font-semibold flex items-center gap-2 animate-bounce">
                                🎉 Hệ thống đã ghi nhận trao quà thành công! Mã thưởng này không thể tái sử dụng.
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Biểu mẫu đã gửi</h2>
                    <p className="text-sm text-gray-500">
                        {submissions.length} biểu mẫu · {submissions.filter(s => s.result_type === 'win').length} người trúng thưởng
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={load}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
                    >
                        🔄 Làm mới
                    </button>
                    {canExport ? (
                        <button
                            onClick={() => exportCSV(filtered)}
                            disabled={filtered.length === 0}
                            className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-40"
                        >
                            📥 Xuất CSV ({filtered.length})
                        </button>
                    ) : (
                        <button
                            onClick={() => alert(`Tính năng Xuất CSV leads yêu cầu nâng cấp lên gói cước hạng Sự kiện trở lên. Gói hiện tại của bạn là: ${entitlements?.plan_label || 'Dùng thử'}`)}
                            className="flex items-center gap-1.5 rounded-lg bg-gray-200 border border-gray-300 px-3 py-2 text-sm font-medium text-gray-400 cursor-not-allowed hover:bg-gray-100"
                            title="Yêu cầu nâng cấp gói cước để Xuất CSV"
                        >
                            🔒 Xuất CSV (Cần nâng cấp)
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <input
                    type="text"
                    placeholder="Tìm theo tên, SĐT, email..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-1 min-w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
                <select
                    value={filterResult}
                    onChange={e => setFilterResult(e.target.value as 'all' | 'win' | 'lose')}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                >
                    <option value="all">Tất cả kết quả</option>
                    <option value="win">Trúng thưởng</option>
                    <option value="lose">Không trúng</option>
                </select>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    ⚠️ {error}
                </div>
            )}

            {/* Empty state */}
            {!error && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
                    <span className="text-4xl">📋</span>
                    <p className="mt-3 text-sm font-medium text-gray-600">Chưa có biểu mẫu nào</p>
                    <p className="mt-1 text-xs text-gray-400">
                        Biểu mẫu sẽ xuất hiện sau khi người chơi tham gia chiến dịch
                    </p>
                </div>
            )}

            {/* Table */}
            {filtered.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">#</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Thời gian</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Người chơi</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Dữ liệu form</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Kết quả</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Mã trúng</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map((s, i) => {
                                // --- Player name logic ---
                                const playerName =
                                    (s.player?.name && s.player.name !== 'Khách chưa đặt tên')
                                        ? s.player.name
                                        : ((s.data?.name as string) && (s.data.name as string) !== 'Khách chưa đặt tên')
                                            ? s.data.name as string
                                            : null;
                                const playerPhone = s.player?.phone || (s.data?.phone as string) || null;
                                const playerEmail = s.player?.email || (s.data?.email as string) || null;
                                const playerRef = s.player?.id
                                    ? `#${s.player.id.slice(-6).toUpperCase()}`
                                    : s.player_id
                                        ? `#${s.player_id.slice(-6).toUpperCase()}`
                                        : null;

                                // --- Form data: show ALL fields with real values ---
                                // Chỉ bỏ qua: field rỗng, giá trị mặc định của hệ thống, field nội bộ
                                const DEFAULT_PLACEHOLDERS = [
                                    'Khách chưa đặt tên', 'khách chưa đặt tên',
                                    'Chưa có', 'N/A', 'n/a', ''
                                ];
                                const formEntries = Object.entries(s.data || {})
                                    .filter(([k, v]) =>
                                        !SKIP_KEYS.includes(k)
                                        && v !== null && v !== undefined
                                        && !DEFAULT_PLACEHOLDERS.includes(String(v).trim())
                                    )
                                    .map(([k, v]) => ({ k, v: String(v) }));
                                const formDataStr = formEntries.map(({ k, v }) => `${k}: ${v}`).join(' | ');

                                // --- Gift tier icon ---
                                const tierIcon = s.gift_tier ? TIER_ICONS[s.gift_tier] || '' : '';

                                return (
                                <tr key={s.id} className={`hover:bg-gray-50 ${s.result_type === 'win' ? 'bg-green-50/30' : ''}`}>
                                    <td className="px-3 py-3 text-xs text-gray-400">{i + 1}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                                        {formatDate(s.submitted_at || s.created_at)}
                                    </td>

                                    {/* Cột Người chơi */}
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">
                                            {playerName ?? (
                                                <span className="italic text-gray-400">
                                                    Ẩn danh {playerRef && <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] font-mono text-gray-500">{playerRef}</span>}
                                                </span>
                                            )}
                                        </div>
                                        {(playerPhone || playerEmail) && (
                                            <div className="mt-0.5 text-xs text-gray-500">
                                                {playerPhone && <span>{playerPhone}</span>}
                                                {playerEmail && <span className="ml-2 text-gray-400">{playerEmail}</span>}
                                            </div>
                                        )}
                                        {/* IP hash rút gọn — chỉ hiện nếu có */}
                                        {(s.ip_hash || s.player?.ip_hash) && (
                                            <div className="mt-1 flex items-center gap-1.5">
                                                <code className="font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" title={s.ip_hash || s.player?.ip_hash}>
                                                    IP·{(s.ip_hash || s.player?.ip_hash || '').slice(0, 8)}
                                                </code>
                                                {(s.shared_ip_count ?? 0) > 1 && (
                                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded-full">
                                                        ⚠️ {s.shared_ip_count}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>

                                    {/* Cột Dữ liệu form */}
                                    <td className="px-4 py-3 max-w-[200px]">
                                        {formDataStr ? (
                                            <div className="text-xs text-gray-700 space-y-0.5">
                                                {formEntries.map(({ k, v }) => (
                                                    <div key={k} className="flex flex-wrap gap-1">
                                                        <span className="text-gray-400">{k}:</span>
                                                        <span className="font-medium text-gray-800 break-all" title={v}>{v.length > 30 ? v.slice(0, 28) + '…' : v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-300" title="Người chơi chưa điền form">Chưa điền</span>
                                        )}
                                    </td>

                                    {/* Cột Kết quả */}
                                    <td className="px-4 py-3">
                                        {s.result_type === 'win' ? (
                                            <div className="flex flex-col gap-1">
                                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                                    🎉 Trúng
                                                </span>
                                                {s.gift_name && (
                                                    <span className="text-xs text-gray-600">
                                                        {tierIcon} {s.gift_name}
                                                    </span>
                                                )}
                                            </div>
                                        ) : s.result_type === 'lose' ? (
                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-400">
                                                May mắn lần sau
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>

                                    {/* Cột Mã trúng */}
                                    <td className="px-4 py-3">
                                        {s.reward_code ? (
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1">
                                                    <code className="rounded bg-amber-50 px-2 py-1 text-xs font-mono font-bold tracking-wider text-amber-700">
                                                        {s.reward_code}
                                                    </code>
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(s.reward_code!)}
                                                        title="Sao chép mã"
                                                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                                    >
                                                        📋
                                                    </button>
                                                </div>
                                                {(claimedCodes.has(s.reward_code) || s.claim_status === 'claimed') ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                                        ✅ Đã trao
                                                    </span>
                                                ) : s.result_type === 'win' && (
                                                    <button
                                                        onClick={() => handleQuickClaim(s.reward_code!)}
                                                        disabled={!!rowClaiming}
                                                        className="rounded bg-green-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                        {rowClaiming === s.reward_code ? 'Đang xử lý...' : '🎁 Trao quà'}
                                                    </button>
                                                )}
                                            </div>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
